import { useEffect, useId, useRef, useState } from "react";
import "./digital-studio.css";
import { HeroVideo } from "@/components/HeroVideo";
import { Footer as SiteFooter } from "@/components/sections/Footer";

const MOMENCE_ORIGIN = "https://momence.com";
const MOMENCE_HOST_ID = "314864";
const MOMENCE_MEMBERSHIP_ID = "850411";

export const MOMENCE_CONFIG = Object.freeze({
  origin: MOMENCE_ORIGIN,
  hostId: MOMENCE_HOST_ID,
  membershipId: MOMENCE_MEMBERSHIP_ID,
  profileUrl: `${MOMENCE_ORIGIN}/u/forte-by-ryann-JTmve4`,
  signInUrl: `${MOMENCE_ORIGIN}/sign-in`,
  schedule: Object.freeze({
    targetId: "ribbon-schedule",
    scriptId: "momence-schedule-embed-script",
    readyElementId: "momence-plugin-host-schedule",
    scriptSrc: `${MOMENCE_ORIGIN}/plugin/host-schedule/host-schedule.js`,
    attributes: Object.freeze({
      host_id: MOMENCE_HOST_ID,
      teacher_ids: "[]",
      location_ids: "[]",
      tag_ids: "[]",
      hide_tags: "true",
      hide_drop_in_price: "true",
      default_filter: "show-all",
      locale: "en",
    }),
  }),
  video: Object.freeze({
    src: `${MOMENCE_ORIGIN}/video/plugin/${MOMENCE_HOST_ID}`,
    height: 1000,
  }),
  membership: Object.freeze({
    purchaseUrl: `${MOMENCE_ORIGIN}/m/${MOMENCE_MEMBERSHIP_ID}`,
  }),
});

export function buildMomenceSignInUrl(returnUrl) {
  try {
    const target = new URL(returnUrl);
    if (target.origin !== MOMENCE_CONFIG.origin) return MOMENCE_CONFIG.signInUrl;

    const signIn = new URL(MOMENCE_CONFIG.signInUrl);
    signIn.searchParams.set("redirect", target.href);
    signIn.searchParams.set("hostId", MOMENCE_CONFIG.hostId);
    return signIn.href;
  } catch (error) {
    return MOMENCE_CONFIG.signInUrl;
  }
}

export const CLASS_FORMATS = {
  strength: {
    name: "Pilates X Strength",
    color: "#c39a63",
    img: "method.jpg",
    days: "Mondays & Wednesdays",
    tagline: "Our muscle-building days.",
    desc: "A dynamic blend of athletic Pilates and progressive strength training. Expect intentional, full-body work that builds week over week. It is the foundation of the Forte method.",
    bring: "Light + heavy weights · Mat",
  },
  sculpt: {
    name: "Pilates X Sculpt",
    color: "#d7b27a",
    img: "foundation.jpg",
    days: "Tuesdays & Thursdays",
    tagline: "Refine, sculpt, and strengthen.",
    desc: "Designed to refine, sculpt, and strengthen the muscle we build on Strength days. Precision Pilates with a burn you'll feel tomorrow.",
    bring: "Light weights · Resistance band · Ball",
  },
  upper: {
    name: "Upper Body, Arms & Abs",
    color: "#b08a57",
    img: "privates.jpg",
    days: "Fridays",
    tagline: "Finish the week strong.",
    desc: "Arms, shoulders, back, and core, with a focused upper-body burn that pairs strength work with deep abdominal training. Walk into the weekend feeling tall.",
    bring: "Light + heavy weights · Mat",
  },
  hiit: {
    name: "High-Intensity Pilates",
    color: "#d3ad77",
    img: "events.jpg",
    days: "Sundays",
    tagline: "Think of this as my version of running.",
    desc: "The precision of Pilates with the intensity of high-energy interval training. A sweaty, athletic reset to launch your week.",
    bring: "Mat · Ankle weights optional",
  },
};

/* Native weekly editorial summary. Momence remains the booking source of truth. */
export const WEEK_TEMPLATE = [
  { day: "Monday",    format: "strength", time: "8:00 AM MT", et: "10:00 AM ET" },
  { day: "Tuesday",   format: "sculpt",   time: "8:00 AM MT", et: "10:00 AM ET" },
  { day: "Wednesday", format: "strength", time: "8:00 AM MT", et: "10:00 AM ET" },
  { day: "Thursday",  format: "sculpt",   time: "8:00 AM MT", et: "10:00 AM ET" },
  { day: "Friday",    format: "upper",    time: "8:00 AM MT", et: "10:00 AM ET" },
  { day: "Sunday",    format: "hiit",     time: "9:00 AM MT", et: "11:00 AM ET" },
];

const FORTE_TIME_ZONE = "America/Denver";
const LAUNCH_COPY_LAST_DATE = "2026-08-02";
const forteDateFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: FORTE_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export function shouldShowLaunchCopy(now = new Date()) {
  const instant = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(instant.getTime())) return false;

  const parts = Object.fromEntries(
    forteDateFormatter
      .formatToParts(instant)
      .filter(({ type }) => type === "year" || type === "month" || type === "day")
      .map(({ type, value }) => [type, value]),
  );
  const forteDate = `${parts.year}-${parts.month}-${parts.day}`;
  return forteDate <= LAUNCH_COPY_LAST_DATE;
}

function useLaunchCopyVisibility() {
  const [showLaunchCopy, setShowLaunchCopy] = useState(() => shouldShowLaunchCopy());

  useEffect(() => {
    if (!showLaunchCopy) return undefined;
    const intervalId = window.setInterval(() => {
      setShowLaunchCopy(shouldShowLaunchCopy());
    }, 60_000);
    return () => window.clearInterval(intervalId);
  }, [showLaunchCopy]);

  return showLaunchCopy;
}

function escapeHtmlAttribute(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll('"', "&quot;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

export function buildMomenceScheduleDocument(config = MOMENCE_CONFIG) {
  const { schedule } = config;
  const scriptAttributes = Object.entries(schedule.attributes)
    .map(([name, value]) => `${name}="${escapeHtmlAttribute(value)}"`)
    .join("\n  ");

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<style>
:root {
  --momenceColorBackground: #F3EBDD;
  --momenceColorPrimary: 138, 99, 62;
  --momenceColorBlack: 24, 20, 17;
  color-scheme: light;
}
html, body { margin: 0; min-height: 100%; background: #F3EBDD; }
</style>
</head>
<body>
<div id="${escapeHtmlAttribute(schedule.targetId)}"></div>
<script
  async
  type="module"
  id="${escapeHtmlAttribute(schedule.scriptId)}"
  ${scriptAttributes}
  src="${escapeHtmlAttribute(schedule.scriptSrc)}"
></script>
<script>
document.addEventListener("click", function(event) {
  var path = typeof event.composedPath === "function" ? event.composedPath() : [event.target];
  var link = path.find(function(node) {
    return node && node.tagName === "A" && node.href;
  });
  if (!link) return;

  try {
    var url = new URL(link.href, document.baseURI);
    var parts = url.pathname.split("/");
    var isBooking = url.origin === ${JSON.stringify(config.origin)} && parts[1] === "s" && parts[2];
    var isSignIn = url.origin === ${JSON.stringify(config.origin)} && /^\\/(sign-in|login)(\\/|$)/.test(url.pathname);
    if (!isBooking && !isSignIn) return;
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === "function") {
      event.stopImmediatePropagation();
    }
    window.open(url.href, "_blank", "noopener,noreferrer");
  } catch (error) {}
}, true);
</script>
</body>
</html>`;
}

export const MOMENCE_SCHEDULE_DOCUMENT = buildMomenceScheduleDocument();
function MomenceSchedule() {
  const frameRef = useRef(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let attempts = 0;
    const inspectFrame = () => {
      attempts += 1;
      try {
        const frameDocument = frameRef.current?.contentDocument;
        if (frameDocument?.getElementById(MOMENCE_CONFIG.schedule.readyElementId)) {
          setStatus("ready");
          window.clearInterval(intervalId);
        } else if (attempts === 48) {
          setStatus("error");
        } else if (attempts >= 240) {
          window.clearInterval(intervalId);
        }
      } catch (error) {
        if (attempts === 48) {
          setStatus("error");
        } else if (attempts >= 240) {
          window.clearInterval(intervalId);
        }
      }
    };

    const intervalId = window.setInterval(inspectFrame, 250);
    inspectFrame();
    return () => window.clearInterval(intervalId);
  }, []);


  return (
    <div className="momence-schedule" aria-busy={status === "loading"}>
      {status !== "ready" && (
        <div className="momence-schedule-placeholder" aria-live="polite">
          {status === "loading" ? (
            <span>Loading the live class schedule…</span>
          ) : (
            <span>
              The schedule did not load here. <a href={MOMENCE_CONFIG.profileUrl} target="_blank" rel="noopener noreferrer">Open it on Momence</a>.
            </span>
          )}
        </div>
      )}
      <iframe
        ref={frameRef}
        className="momence-schedule-iframe"
        srcDoc={MOMENCE_SCHEDULE_DOCUMENT}
        title="Live Momence class schedule"
        loading="eager"
      ></iframe>
    </div>
  );
}
function MomenceEmbed({ title, caption, fallback, children }) {
  return (
    <div className="momence-frame">
      <div className="momence-frame-head">
        <span className="embed-title">{title}</span>
        <span className="integration-source">Booking and access by Momence</span>
      </div>
      {children}
      {(caption || fallback) && (
        <p className="momence-caption">
          {caption && <span>{caption}</span>}
          {fallback && (
            <a
              className="momence-fallback"
              href={fallback.href}
              target="_blank"
              rel="noopener noreferrer"
            >{fallback.label} {"\u2197"}</a>
          )}
        </p>
      )}
    </div>
  );
}

function useModalControls(onClose) {
  const dialogRef = useRef(null);
  const initialFocusRef = useRef(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    initialFocusRef.current?.focus();

    const onKey = (event) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusable = [...dialogRef.current.querySelectorAll(
        'a[href], button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])'
      )].filter((node) => !node.hasAttribute("hidden"));
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) document.body.style.paddingRight = scrollbarWidth + "px";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
      previouslyFocused?.focus?.();
    };
  }, [onClose]);

  return { dialogRef, initialFocusRef };
}

function ClassModal({ formatKey, time, et, onClose, onBook }) {
  const format = CLASS_FORMATS[formatKey];
  const titleId = useId();
  const { dialogRef, initialFocusRef } = useModalControls(onClose);

  return (
    <div className="ds-modal-overlay" onClick={onClose}>
      <div
        className="ds-modal class-modal"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="class-modal-hero">
          <img src={"/media/" + format.img} alt=""/>
          <div className="class-modal-hero-vignette"></div>
          <button ref={initialFocusRef} className="ds-modal-close class-modal-x" type="button" onClick={onClose} aria-label="Close class details">×</button>
          <div className="class-modal-hero-text">
            <span className="class-session">{format.days} · {time} / {et}</span>
            <h3 id={titleId}>{format.name}</h3>
          </div>
        </div>
        <div className="class-modal-body">
          <p className="class-tagline">{format.tagline}</p>
          <p className="class-desc">{format.desc}</p>
          <div className="class-meta">
            <div className="class-meta-item">
              <span className="cm-label">What to bring</span>
              <span className="cm-value">{format.bring}</span>
            </div>
            <div className="class-meta-item">
              <span className="cm-label">Included</span>
              <span className="cm-value">No extra charge with membership. Replay access when published.</span>
            </div>
          </div>
          <div className="class-modal-ctas">
            <button className="btn-fx btn-primary" type="button" onClick={onBook}>Book in Live Schedule</button>
            <a
              className="class-back-action"
              href={MOMENCE_CONFIG.membership.purchaseUrl}
              target="_blank"
              rel="noopener noreferrer"
            >Not a member? Join for $45 / month {"\u2197"}</a>
          </div>
        </div>
      </div>
    </div>
  );
}

function MomenceVideoLibrary() {
  const accountUrl = buildMomenceSignInUrl(MOMENCE_CONFIG.profileUrl);

  return (
    <div className="momence-library">
      <iframe
        className="momence-video-library"
        src={MOMENCE_CONFIG.video.src}
        title="Browse the Forte video library"
        height={MOMENCE_CONFIG.video.height}
        loading="lazy"
        allow="fullscreen"
      ></iframe>
      <div className="library-access-bar">
        <p>Browse the collection here. Open Momence in a full tab for member sign-in and protected playback.</p>
        <div className="library-access-actions">
          <a
            className="btn-fx btn-primary"
            href={MOMENCE_CONFIG.video.src}
            target="_blank"
            rel="noopener noreferrer"
          >Open library to watch</a>
          <a
            className="account-link"
            href={accountUrl}
            target="_blank"
            rel="noopener noreferrer"
          >Manage my Momence account {"\u2197"}</a>
        </div>
      </div>
    </div>
  );
}
function MembershipBenefits() {
  return (
    <section className="section studio-benefits-section" id="benefits" aria-labelledby="studio-benefits-title">
      <div className="section-inner">
        <h2 className="headline membership-heading" id="studio-benefits-title">
          The full studio. <em>$45 a month.</em>
        </h2>
        <div className="benefit-list">
          <div className="benefit">
            <span className="b-num">01</span>
            <div>
              <h3>Live Zoom classes</h3>
              <p>Six coached sessions each week across Strength, Sculpt, Upper Body, and Sunday HIIT.</p>
            </div>
          </div>
          <div className="benefit">
            <span className="b-num">02</span>
            <div>
              <h3>The complete video library</h3>
              <p>All member-enabled workouts and published replays at no additional charge while active.</p>
            </div>
          </div>
          <div className="benefit">
            <span className="b-num">03</span>
            <div>
              <h3>Programmed progression</h3>
              <p>New work built on the same deliberate progression as the Forte method.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function LiveClasses({ onSelectClass, showLaunchCopy }) {
  return (
    <section className="section" id="live">
      <div className="section-inner">
        <div className="live-intro">
          <h2 className="headline stacked-headline"><span>Live classes.</span><em>Real coaching.</em></h2>
          <p className="lede">
            The Denver studio experience, streamed. Ryann coaches in real time, and the class replays are published into the library after class.
          </p>
        </div>
        <div className="live-stack">
          <div className="schedule-card" id="weekly-rhythm" aria-labelledby="weekly-rhythm-title">
            <h3 id="weekly-rhythm-title">Weekly rhythm</h3>
            <div
              className="weekly-rhythm-scroll"
              role="region"
              aria-label="Weekly live class rhythm"
              tabIndex="0"
            >
              <div className="weekly-rhythm-grid">
                {WEEK_TEMPLATE.map((day) => (
                  <button type="button" className="sched-row sched-row-btn" key={day.day} onClick={() => onSelectClass(day)}>
                    <img className="class-thumb" src={"/media/" + CLASS_FORMATS[day.format].img} alt=""/>
                    <div className="sched-row-text">
                      <span className="day">{day.day}</span>
                      <span className="format" style={{color: CLASS_FORMATS[day.format].color}}>
                        {CLASS_FORMATS[day.format].name}
                      </span>
                    </div>
                    <span className="time"><b>{day.time}</b><br/>{day.et}</span>
                    <span className="sched-row-arrow" aria-hidden="true">{"\u2197"}</span>
                  </button>
                ))}
              </div>
            </div>
            <p className="sched-note">
              <b>{showLaunchCopy ? "First class: Sunday, August 2 \u00B7 9:00 AM MT." : "Live every week \u00B7 Monday-Friday + Sunday."}</b><br/>
              Tap a class for format details. Use the live Momence schedule to reserve the exact session.
            </p>
          </div>
          <div className="momence-booking" id="schedule">
            <span id="momence-booking" aria-hidden="true"/>
            <MomenceEmbed
              title={"Upcoming classes \u00B7 book your spot"}
              fallback={{href: MOMENCE_CONFIG.profileUrl, label: "Open schedule in Momence"}}
            >
              <MomenceSchedule/>
            </MomenceEmbed>
          </div>
        </div>
      </div>
    </section>
  );
}

function Library() {
  return (
    <section className="section library-section" id="library">
      <div className="section-inner">
        <div className="library-intro">
          <h2 className="headline stacked-headline"><span>Every workout.</span><em>On your schedule.</em></h2>
          <p className="lede">
            Browse Forte's on-demand collection and published class replays.
          </p>
        </div>
        <MomenceEmbed
          title="The Forte video library"
        >
          <MomenceVideoLibrary/>
        </MomenceEmbed>
      </div>
    </section>
  );
}

function Membership() {
  return (
    <section className="section member-section" id="membership">
      <div className="section-inner">
        <div className="membership-offer">
          <div className="price-card">
            <span className="member-access">Unlimited live + on-demand access</span>
            <div className="pc-name">Forte Digital Studio</div>
            <div className="pc-amount">$45</div>
            <div className="pc-per">per month {"\u00B7"} cancel anytime</div>
            <ul className="pc-includes">
              <li>Live classes, Monday-Friday + Sunday</li>
              <li>Full member-enabled on-demand library</li>
              <li>Published class replays</li>
              <li>New programming every week</li>
            </ul>
            <a
              className="btn-fx btn-primary"
              href={MOMENCE_CONFIG.membership.purchaseUrl}
              target="_blank"
              rel="noopener noreferrer"
            >Join for $45 / month</a>
            <a
              className="account-link price-card-account-link"
              href={buildMomenceSignInUrl(MOMENCE_CONFIG.profileUrl)}
              target="_blank"
              rel="noopener noreferrer"
            >Manage an existing membership {"\u00A0\u2197"}</a>
          </div>
        </div>
      </div>
    </section>
  );
}

function TransitionNote() {
  return (
    <section className="transition-note" id="transition">
      <div className="note-inner">
        <p className="transition-location">Denver to Scottsdale</p>
        <p className="transition-statement">
          The studio is moving. The method is staying with you.
        </p>
        <p className="transition-detail">Same programming, same coaching, same community. Now it fits in your living room.</p>
        <p className="transition-signoff">Ryann</p>
      </div>
    </section>
  );
}

export default function DigitalStudio() {
  const showLaunchCopy = useLaunchCopyVisibility();
  const [selectedClass, setSelectedClass] = useState(null);

  const openScheduleFromClass = () => {
    setSelectedClass(null);
    window.setTimeout(() => {
      const schedule = document.getElementById("schedule");
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      schedule?.scrollIntoView({
        behavior: reduceMotion ? "auto" : "smooth",
        block: "start",
      });
    }, 0);
  };

  return (
    <>
      <main>
        <HeroVideo
          id="studio"
          testId="digital-studio-hero"
          title="FORTE"
          subtitle="Digital Studio"
          tagline={{
            lead: "Live classes.",
            middle: "On demand.",
            end: "Anywhere.",
          }}
          primaryAction={{
            label: "Join for $45 / month",
            href: MOMENCE_CONFIG.membership.purchaseUrl,
            target: "_blank",
            rel: "noopener noreferrer",
            testId: "digital-studio-hero-join",
            icon: "up-right",
          }}
          secondaryAction={{
            label: "View Live Schedule",
            href: "#live",
            testId: "digital-studio-hero-live",
            icon: "down",
          }}
          tertiaryAction={{
            label: "View the On-Demand Library",
            href: "#library",
            testId: "digital-studio-hero-library",
            icon: "down",
          }}
          note={
            showLaunchCopy
              ? "First live class: Sunday, August 2"
              : "Six live classes every week"
          }
        />
        <div className="digital-studio-page">
          <MembershipBenefits/>
          <LiveClasses
            onSelectClass={setSelectedClass}
            showLaunchCopy={showLaunchCopy}
          />
          <Library/>
          <Membership/>
          <TransitionNote/>
          {selectedClass && (
            <ClassModal
              formatKey={selectedClass.format}
              time={selectedClass.time}
              et={selectedClass.et}
              onClose={() => setSelectedClass(null)}
              onBook={openScheduleFromClass}
            />
          )}
        </div>
      </main>
      <SiteFooter/>
    </>
  );
}